import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getDocumentFromAPI } from '../services/eCMRService';
import { validateECMRDocumentFromAPI } from '../services/schemaValidator';

/**
 * Componente para validar un documento eCMR contra el baseline tecnico UNECE eCMR/CMR.
 */
const ValidateECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Cloud-only production mode: legacy source=local validation links are treated as API document links.
  const source: 'api' = 'api';
  const [documentTitle, setDocumentTitle] = useState<string>('Sin título');
  const [xmlContent, setXmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationResults, setValidationResults] = useState<{ valid: boolean, errors?: string[] } | null>(null);
  const [validating, setValidating] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const apiDocument = await getDocumentFromAPI(id);
        const apiXml = apiDocument.xmlContent || apiDocument.content || '';
        if (!apiXml) {
          setError('El documento API no contiene XML para validar');
          setLoading(false);
          return;
        }
        setDocumentTitle(apiDocument.title || apiDocument.document_id || id);
        setXmlContent(apiXml);
        setLoading(false);
      } catch (err) {
        setError('No se pudo cargar el documento e-CMR para validación. Inténtelo de nuevo.');
        setLoading(false);
      }
    };

    loadData();
  }, [id, source]);

  const handleValidate = async () => {
    if (!xmlContent || !id) return;
    
    setValidating(true);
    
    try {
      const result = await validateECMRDocumentFromAPI(id);
      setValidationResults(result);
    } catch (error) {
      setError('No se pudo validar el documento e-CMR. Inténtelo de nuevo.');
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }


  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Validar e-CMR: {documentTitle}
        </h1>
        
        <div className="flex space-x-2">
          <button
            onClick={handleValidate}
            disabled={validating}
            className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              validating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {validating ? 'Validando...' : 'Validar XML almacenado en backend'}
          </button>
          
          <Link
            to={`/view/${id}?source=api`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Ver documento
          </Link>
          
          <button
            onClick={() => navigate('/')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Volver
          </button>
        </div>
      </div>
      
      {validationResults && (
        <div className={`mb-6 p-4 rounded ${validationResults.valid ? 'bg-green-100 border-l-4 border-green-500 text-green-700' : 'bg-red-100 border-l-4 border-red-500 text-red-700'}`}>
          <h2 className="text-xl font-semibold mb-2">
            {validationResults.valid 
              ? 'El XML almacenado supero las validaciones tecnicas UNECE eCMR/CMR disponibles'
              : 'El XML almacenado no supero las validaciones tecnicas UNECE eCMR/CMR disponibles'}
          </h2>
          {source === 'api' && (
            <p className="text-sm mb-2">
              Validacion document-bound auditable sobre el XML almacenado en el backend como baseline tecnico parcial; no es certificacion legal completa.
            </p>
          )}
          
          {validationResults.errors && validationResults.errors.length > 0 && (
            <div className="mt-2">
              <h3 className="font-bold">Problemas encontrados:</h3>
              <ul className="list-disc pl-5 mt-2">
                {validationResults.errors.map((error, index) => (
                  <li key={index} className="text-sm mt-1">{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded p-6">
        <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Informacion del baseline tecnico</h2>
        <div className="mb-4">
          <p>
            Los documentos e-CMR se apoyan en perfiles UNECE eCMR locales, CMR 1956 y el Protocolo eCMR 2008.
            El backend aplica el runtime XML/XSD controlado por defecto y D24A permanece solo como interoperabilidad explícita.
            Esta pantalla comprueba el baseline tecnico disponible para el XML almacenado:
          </p>
          <ul className="list-disc pl-5 mt-2">
            <li>Estructura del documento segun el esquema XSD local seleccionado por perfil declarado o por el default controlado del backend</li>
            <li>Presencia de campos obligatorios</li>
            <li>Formato correcto de los datos (fechas, codigos, etc.)</li>
            <li>Validez de codigos contra las listas locales soportadas</li>
            <li>Compuertas CMR/eCMR saneadas cuando el backend las devuelve</li>
          </ul>
        </div>
        
        <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Esquemas XSD de referencia</h2>
        <div className="mb-4">
          <ul className="list-disc pl-5">
            <li>Esquema principal UNECE eCMR local seleccionado por perfil declarado</li>
            <li>ReusableAggregateBusinessInformationEntity local</li>
            <li>QualifiedDataType local</li>
            <li>UnqualifiedDataType local</li>
            <li>Listas locales UNECE/ISO admitidas por el backend</li>
            <li>Y otros esquemas de códigos relacionados</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ValidateECMR;
